import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { IoPerson } from "react-icons/io5";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { MdLocationPin } from "react-icons/md";
import { IoMdSettings } from "react-icons/io";
import { BsBank2 } from "react-icons/bs";
import { PiSirenBold } from "react-icons/pi";

// ── Helpers ───────────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return null;
  const [y, m, d] = val.split('-');
  if (!y || !m || !d) return val;
  return `${d}-${m}-${y}`;
};

const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? `${age} years` : null;
};

// ── UI Primitives ─────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = 'from-slate-700 to-slate-900' }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br ${color} text-white text-sm shadow-md`}>
      {icon}
    </div>
    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-600">{title}</h3>
    <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent ml-2" />
  </div>
);

const InfoCell = ({ label, value }) => (
  <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    <span className="text-sm font-medium text-slate-800 break-words">
      {value || <span className="text-slate-300 italic">—</span>}
    </span>
  </div>
);

const AddressCell = ({ label, value }) => (
  <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    <span className="text-sm font-medium text-slate-800 whitespace-pre-line leading-relaxed">
      {value || <span className="text-slate-300 italic">—</span>}
    </span>
  </div>
);

const PhoneCell = ({ label, value }) => (
  <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    {value ? (
      <div className="flex items-center gap-2">
        <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
        <span className="text-sm font-medium text-slate-800">{value}</span>
      </div>
    ) : (
      <span className="text-slate-300 italic text-sm">—</span>
    )}
  </div>
);

const ContactCard = ({ label, number }) => (
  <div className="flex flex-col gap-2 p-4 rounded-xl bg-slate-50/60 border border-slate-100">
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    {!number ? (
      <span className="text-sm text-slate-300 italic">—</span>
    ) : (
      <div className="flex items-center gap-2">
        <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
        <span className="text-sm font-medium text-slate-800">{number}</span>
      </div>
    )}
  </div>
);

// ── Component ─────────────────────────────────────────────────
function ViewEmployee() {
  const { empid } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/employee/${empid}`)
      .then((res) => {
        setEmp(res.data)
      console.log('Employee data fetched successfully:', res.data);})
      .catch((err) => console.error('Failed to fetch employee', err))
      .finally(() => setLoading(false));
  }, [empid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          <p className="text-sm text-slate-400 tracking-wider uppercase">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 sm:px-8 py-8">

      {/* ── Page Header ── */}
      <div className="w-full mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
         
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">Human Resources</p>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              {emp.name || 'Employee Profile'}
            </h1>
            {(emp.designation || emp.department || emp.location) && (
              <p className="text-sm text-slate-500 mt-0.5">
                {[emp.designation, emp.department, emp.location].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/hr/employees/EditEmployee/${empid}`)}
            className="flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-xl 
              bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-600 hover:to-slate-800
              transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors px-4 py-2 rounded-xl hover:bg-white border border-transparent hover:border-slate-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>
      </div>

      <div className="w-full space-y-5">

        {/* ── 1. Basic Information ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<IoPerson />} title="Basic Information" color="from-indigo-500 to-indigo-700" />

          <div className="flex flex-col sm:flex-row gap-8 items-start">
            {/* Profile photo — matches AddEmployee uploader size/shape */}
            <div className="flex-shrink-0 flex flex-col items-center gap-2">
              {emp.profilePictureUrl ? (
                <div className="w-28 h-28 rounded-full border-2 border-dashed border-slate-300 overflow-hidden">
                  <img
                    src={emp.profilePictureUrl}
                    alt={emp.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-28 h-28 rounded-full border-2 border-dashed border-slate-300
                  bg-slate-50 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-1 text-slate-400">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Photo</span>
                  </div>
                </div>
              )}
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Profile Photo</span>
            </div>

            {/* Info cells grid */}
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <InfoCell label="Employee ID" value={emp.employeeId} />
              <InfoCell label="Employee Full Name" value={emp.name} />
              <InfoCell label="Employee Nick Name" value={emp.shortName} />
              <InfoCell label="Date of Joining" value={fmtDate(emp.dateOfJoining)} />
              <InfoCell label="Designation" value={emp.designation} />
              <InfoCell label="Department" value={emp.department} />
              <InfoCell label="Location" value={emp.location} />
              <InfoCell label="Date of Birth" value={fmtDate(emp.dateOfBirth)} />
              <InfoCell label="Age" value={calcAge(emp.dateOfBirth)} />
              <InfoCell label="Gender" value={emp.gender} />
            </div>
          </div>
        </div>

        {/* ── 2. Personal Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<BsFillPersonLinesFill />} title="Personal Details" color="from-violet-500 to-violet-700" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <InfoCell label="Email ID" value={emp.email} />
            <PhoneCell label="Phone Number" value={emp.contactNumber} />
            <InfoCell label="Blood Group" value={emp.bloodGroup} />
            <InfoCell label="Marital Status" value={emp.maritalStatus} />
            <InfoCell label="Father's Name" value={emp.fatherName} />
            <InfoCell label="Mother's Name" value={emp.motherName} />
            <InfoCell label="Spouse Name" value={emp.spouseName} />
            <InfoCell label="Religion" value={emp.religion} />
            <InfoCell label="Medical Issues" value={emp.medicalHistory} />
            <InfoCell label="Personal Identification Mark" value={emp.personalIdentificationMarks} />
          </div>
        </div>

        {/* ── 3. Address Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<MdLocationPin />} title="Address Details" color="from-emerald-500 to-emerald-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AddressCell label="Present Address" value={emp.presentAddress} />
            <AddressCell label="Permanent Address" value={emp.permanentAddress} />
          </div>
        </div>

        {/* ── 4. Emergency Contacts ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<PiSirenBold />} title="Emergency Contacts" color="from-rose-500 to-rose-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ContactCard label="Local Contact" number={emp.emergencyContactLocal} />
            <ContactCard label="Immediate Family Contact" number={emp.emergencyContactFamily} />
          </div>
        </div>

        {/* ── 5. Bank & Statutory Details ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<BsBank2 />} title="Bank & Statutory Details" color="from-amber-500 to-amber-700" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <InfoCell label="Bank Name & Branch" value={emp.bankName} />
            <InfoCell label="IFSC Code" value={emp.ifsccode} />
            <InfoCell label="Account Number" value={emp.accountNumber} />
            <InfoCell label="Aadhar Number" value={emp.aadarNo} />
            <InfoCell label="PAN Number" value={emp.panno} />
            <InfoCell label="UAN Number" value={emp.uanno} />
          </div>
        </div>

        {/* ── 6. Role & Assignment ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <SectionHeader icon={<IoMdSettings />} title="Role & Assignment" color="from-slate-600 to-slate-800" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <InfoCell label="Role" value={emp.role} />
            <InfoCell label="Department" value={emp.department} />
            <InfoCell label="Reporting Manager" value={emp.reportingManager} />
          </div>
        </div>

      </div>
    </div>
  );
}

export default ViewEmployee;